<template>
    <div>
        <div class="row tw-mb-4">
            <div class="col-md-6">
                <label class="required form-col-label control-label">{{
                    $t("bankProfile.bankName")
                }}</label>
                <Input
                    v-model="model.bank_name"
                    :placeholder="$t('bankProfile.bankName')"
                    :class="{ 'is-invalid': validate.has('bank_name') }"
                />
                <div class="invalid-feedback" v-if="validate.has('bank_name')">
                    {{ validate.first("bank_name") }}
                </div>
            </div>
        </div>

        <!-- Hint shown on create (before bank is saved) -->
        <div v-if="!model.bank_id" class="tw-mt-2">
            <Alert show-icon>
                {{ $t("bankProfile.saveToAddAccounts") }}
            </Alert>
        </div>

        <!-- Accounts section (shown on edit) -->
        <div v-if="model.bank_id">
            <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                <h6 class="tw-font-semibold tw-mb-0">{{ $t("bankProfile.accounts") }}</h6>
                <Button size="small" type="primary" ghost @click="openAddAccount">
                    <i class="fas fa-plus"></i> {{ $t("addNew") }}
                </Button>
            </div>

            <Table
                :columns="accountColumns"
                :data="accounts"
                size="small"
                :loading="loadingAccounts"
                stripe
            >
                <template slot-scope="{ row, index }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-1"
                        @click.prevent="openEditAccount(row, index)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="18" />
                    </a>
                    <Poptip
                        :title="$t('are_you_sure_to_delete')"
                        :transfer="true"
                        width="180"
                    >
                        <a class="ml-1 text-danger" v-tooltip="$t('delete')" :disabled="row._deleting">
                            <i class="fas fa-circle-notch fa-spin" v-if="row._deleting"></i>
                            <Icon type="ios-trash" size="18" v-else />
                        </a>
                        <div slot="content" class="tw-text-right tw-mt-2">
                            <a
                                class="ivu-btn ivu-btn-danger ivu-btn-small"
                                @click.prevent="onDeleteAccount(row)"
                            >{{ $t("ok") }}</a>
                        </div>
                    </Poptip>
                </template>
            </Table>

            <!-- Add / Edit account inline form -->
            <div v-if="accountForm.visible" class="tw-mt-3 tw-p-3 tw-border tw-rounded tw-bg-gray-50">
                <div class="row">
                    <div class="col-md-5 tw-mb-2">
                        <label class="required form-col-label control-label">{{ $t("bankProfile.accountNo") }}</label>
                        <Input
                            v-model="accountForm.account_no"
                            :placeholder="$t('bankProfile.accountNo')"
                            :class="{ 'is-invalid': accountErrors.has('account_no') }"
                        />
                        <div class="invalid-feedback" v-if="accountErrors.has('account_no')">
                            {{ accountErrors.first("account_no") }}
                        </div>
                    </div>
                    <div class="col-md-5 tw-mb-2">
                        <label class="required form-col-label control-label">{{ $t("bankProfile.accountName") }}</label>
                        <Input
                            v-model="accountForm.account_name"
                            :placeholder="$t('bankProfile.accountName')"
                            :class="{ 'is-invalid': accountErrors.has('account_name') }"
                        />
                        <div class="invalid-feedback" v-if="accountErrors.has('account_name')">
                            {{ accountErrors.first("account_name") }}
                        </div>
                    </div>
                    <div class="col-md-2 tw-mb-2 tw-flex tw-items-end tw-space-x-1">
                        <ts-button
                            color="primary"
                            size="small"
                            :waiting="accountForm.saving"
                            @click.prevent="onSaveAccount"
                        >{{ $t("save") }}</ts-button>
                        <ts-button size="small" @click.prevent="closeAccountForm">{{ $t("cancel") }}</ts-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import bankAccount from "@/api/finance/bank-account";

export default {
    name: "BankProfileFormInput",
    props: {
        value: { type: Object, required: true },
        validate: { type: Object, required: true }
    },
    data() {
        return {
            accounts: [],
            loadingAccounts: false,
            accountForm: {
                visible: false,
                saving: false,
                account_id: null,
                account_no: "",
                account_name: ""
            },
            accountErrors: new Errors()
        };
    },
    computed: {
        model() {
            return this.value;
        },
        accountColumns() {
            return [
                { title: this.$t("bankProfile.accountNo"), key: "account_no", minWidth: 160 },
                { title: this.$t("bankProfile.accountName"), key: "account_name", minWidth: 200 },
                { title: this.$t("actions"), slot: "action", align: "center", width: 90 }
            ];
        }
    },
    watch: {
        "model.bank_id": {
            immediate: true,
            handler(id) {
                if (id) this.loadAccounts(id);
            }
        }
    },
    methods: {
        async loadAccounts(bankId) {
            this.loadingAccounts = true;
            try {
                const res = await bankAccount.index(bankId);
                this.accounts = res.data;
            } finally {
                this.loadingAccounts = false;
            }
        },
        openAddAccount() {
            this.accountForm = { visible: true, saving: false, account_id: null, account_no: "", account_name: "" };
            this.accountErrors = new Errors();
        },
        openEditAccount(row) {
            this.accountForm = { visible: true, saving: false, account_id: row.account_id, account_no: row.account_no, account_name: row.account_name };
            this.accountErrors = new Errors();
        },
        closeAccountForm() {
            this.accountForm.visible = false;
        },
        async onSaveAccount() {
            this.accountErrors = new Errors();
            this.accountForm.saving = true;
            const payload = {
                bank_id: this.model.bank_id,
                account_no: this.accountForm.account_no,
                account_name: this.accountForm.account_name
            };
            try {
                if (this.accountForm.account_id) {
                    await bankAccount.update(this.accountForm.account_id, payload);
                } else {
                    await bankAccount.store(this.model.bank_id, payload);
                }
                this.closeAccountForm();
                await this.loadAccounts(this.model.bank_id);
            } catch (err) {
                if (err.errors) this.accountErrors = new Errors(err.errors);
            } finally {
                this.accountForm.saving = false;
            }
        },
        async onDeleteAccount(row) {
            row._deleting = true;
            try {
                await bankAccount.destroy(row.account_id);
                await this.loadAccounts(this.model.bank_id);
            } finally {
                row._deleting = false;
            }
        }
    }
};
</script>
